/**
 * Spec 228 — resolve a Kridt CSS custom property to a concrete colour for
 * renderers that paint from JS values rather than CSS, and therefore know
 * nothing about tokens (e.g. Canvas2D via react-force-graph-2d's `nodeColor`
 * / node `color` field — see LazyForceGraph consumers). Per the hub design
 * system's guidance for this kind of renderer: read the colour with
 * `getComputedStyle` at mount and re-read it whenever the theme changes,
 * rather than baking in a hex literal that can't follow the theme.
 */
import { useEffect, useState } from 'react';

/** Reads a CSS custom property's current computed value off an element (the
 * document root by default). Returns '' if it can't be read yet (property
 * not defined, or called outside a browser). */
export function readCssVar(name: string, root: HTMLElement | null = null): string {
  if (typeof window === 'undefined') return '';
  const el = root ?? document.documentElement;
  return getComputedStyle(el).getPropertyValue(name).trim();
}

/** The `color` of an element, as a fallback for when a token reads empty —
 * a real resolved colour (inherits the ink scale), never a hardcoded hex. */
function fallbackColor(): string {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.body).color;
}

function resolve(varName: string): string {
  return readCssVar(varName) || fallbackColor();
}

/**
 * React hook: resolves a CSS custom property (e.g. `--hub-color-action`) to
 * its current concrete colour, and re-resolves whenever the theme changes —
 * either the app's own `data-theme` attribute (the light/dark override from
 * `useTheme()`) or the OS's `prefers-color-scheme` (the 'system' preference,
 * which sets no attribute at all). Consumers that hand this value to a
 * non-CSS renderer (canvas, WebGL, an imperative chart library) should
 * include the returned string in their own memo/effect dependencies so the
 * drawing recomputes on flip.
 */
export function useResolvedTokenColor(varName: string): string {
  const [color, setColor] = useState<string>(() => resolve(varName));

  useEffect(() => {
    const update = () => setColor(resolve(varName));
    update();

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', update);

    return () => {
      observer.disconnect();
      media.removeEventListener('change', update);
    };
  }, [varName]);

  return color;
}
